// Adapters from the shadcn-style props the blog components were written against
// (`@/components/ui/*`, which osprey doesn't have) onto osprey's own shared
// components and tokens — keeps the blog UI on the house design system.
import type { ReactNode, ButtonHTMLAttributes } from 'react';
import SharedButton from '../../../shared/components/Button';

type ShadcnButtonVariant = 'default' | 'secondary' | 'outline' | 'ghost' | 'destructive';
type ShadcnButtonSize = 'default' | 'sm' | 'lg' | 'icon';

const buttonVariantMap: Record<ShadcnButtonVariant, 'primary' | 'secondary' | 'outline' | 'transparent'> = {
  default: 'primary',
  secondary: 'secondary',
  outline: 'outline',
  ghost: 'transparent',
  destructive: 'primary',
};
const buttonSizeMap: Record<ShadcnButtonSize, 'sm' | 'md' | 'lg'> = { default: 'md', sm: 'sm', lg: 'lg', icon: 'sm' };

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  variant?: ShadcnButtonVariant;
  size?: ShadcnButtonSize;
}

export const Button = ({ variant = 'default', size = 'default', className = '', ...rest }: ButtonProps) => (
  <SharedButton
    variant={buttonVariantMap[variant]}
    size={buttonSizeMap[size]}
    className={`${variant === 'destructive' ? 'text-red-500' : ''} ${className}`}
    {...rest}
  />
);

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';
const badgeStyles: Record<BadgeVariant, string> = {
  default: 'bg-[#030208] text-[#FBFBFE]',
  secondary: 'bg-muted text-foreground',
  destructive: 'bg-red-100 text-red-700',
  outline: 'border border-[#B8B5B6] text-foreground',
};

export const Badge = ({ variant = 'default', className = '', children }: { variant?: BadgeVariant; className?: string; children: ReactNode }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${badgeStyles[variant]} ${className}`}>
    {children}
  </span>
);
